import type { NewsletterTextAlignment } from "../../types/src";
export type { NewsletterTextAlignment } from "../../types/src";
import { decodeHTML } from "entities";

/** Restricted rich text shared by event generation, the editor and email rendering. */
export function escapeNewsletterText(value: string) {
  return String(value || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function decodeEntities(value: string) {
  return decodeHTML(value).replace(/\u00a0/g, " ");
}
export function sanitizeNewsletterHtml(value: string | null | undefined): string {
  const source = String(value || "").replace(/<!--[^]*?-->/g, "").replace(/<(script|style|iframe|object|svg|math)\b[^>]*>[^]*?<\/\1\s*>/gi, "");
  const stack: string[] = [];
  let output = "";
  for (const token of source.match(/<[^>]*>|[^<]+|</g) || []) {
    if (!token.startsWith("<") || token === "<") { output += escapeNewsletterText(decodeEntities(token)); continue; }
    const match = token.match(/^<\s*(\/?)\s*([a-z0-9]+)/i);
    if (!match) continue;
    const aliases: Record<string,string> = {b:"strong",i:"em",div:"p"};
    const tag = aliases[match[2].toLowerCase()] || match[2].toLowerCase();
    if (!["strong","em","u","p","br"].includes(tag)) continue;
    if (tag === "br") { if (!match[1]) output += "<br>"; continue; }
    if (match[1]) {
      const index = stack.lastIndexOf(tag);
      if (index >= 0) while (stack.length > index) output += `</${stack.pop()}>`;
    } else {
      if (tag === "p" && stack.includes("p")) while (stack.length) output += `</${stack.pop()}>`;
      stack.push(tag); output += `<${tag}>`;
    }
  }
  while (stack.length) output += `</${stack.pop()}>`;
  return output.trim();
}
export function newsletterHtmlToText(value: string): string {
  return decodeEntities(sanitizeNewsletterHtml(value).replace(/<br>/g,"\n").replace(/<\/p>/g,"\n\n").replace(/<[^>]*>/g,"")).replace(/\n{3,}/g,"\n\n").trim();
}
export function newsletterTextToHtml(value: string): string {
  return String(value || "").split(/\n\s*\n/).map(p => `<p>${escapeNewsletterText(p).replace(/\n/g,"<br>")}</p>`).join("");
}
export function renderNewsletterText(text: string, html?: string): string {
  return (html ? sanitizeNewsletterHtml(html) : newsletterTextToHtml(text)).replace(/<p>/g,'<p style="margin:0 0 16px;">');
}
export function normalizeNewsletterImageHeight(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.round(Math.max(100,Math.min(600,value))) : 300;
}

/** Unknown values cannot become email CSS; absence means inherit at section level. */
export function normalizeNewsletterTextAlignment(value: unknown): NewsletterTextAlignment | undefined {
  return value === "left" || value === "center" || value === "right" || value === "justify" ? value : undefined;
}
